import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';

export default function MessagingSettingsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['messaging-settings'],
    queryFn: () => apiService.getMessagingSettingsAdmin(),
  });

  const [loserTemplates, setLoserTemplates] = useState<string[]>([]);
  const [adminNotifyEmail, setAdminNotifyEmail] = useState('');
  const [notifyMatch, setNotifyMatch] = useState(true);
  const [notifyMessage, setNotifyMessage] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (!data) return;
    setLoserTemplates([...(data.loser_message_templates ?? [])]);
    setAdminNotifyEmail(data.admin_activity_notify_email ?? '');
    setNotifyMatch(data.notify_particulier_match ?? true);
    setNotifyMessage(data.notify_particulier_message ?? true);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const normalized = loserTemplates.map((line) => line.trim()).filter((line) => line !== '');
      const email = adminNotifyEmail.trim();
      return apiService.updateMessagingSettingsAdmin({
        loser_message_templates: normalized.length > 0 ? normalized : null,
        admin_activity_notify_email: email !== '' ? email : null,
        notify_particulier_match: notifyMatch,
        notify_particulier_message: notifyMessage,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messaging-settings'] });
      setSaveSuccess(true);
      window.setTimeout(() => setSaveSuccess(false), 3000);
    },
  });

  const [testBanner, setTestBanner] = useState<string | null>(null);
  const testNotifyMutation = useMutation({
    mutationFn: () => apiService.testMessagingAdminNotify(),
    onSuccess: (res) => {
      setTestBanner(res.message || 'E-mail de test envoyé.');
      window.setTimeout(() => setTestBanner(null), 5000);
    },
  });

  const handleAddTemplate = () => {
    if (loserTemplates.length >= 20) return;
    setLoserTemplates((prev) => [...prev, '']);
  };

  const saveErrorMessage = (() => {
    const err = saveMutation.error as { response?: { data?: { error?: string } }; message?: string } | null;
    return err?.response?.data?.error || err?.message || 'Impossible d’enregistrer les réglages.';
  })();

  return (
    <Box sx={{ p: 3, maxWidth: 720 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Messages messagerie
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Messages type proposés au <strong>perdeur</strong> dans la messagerie, et alertes e-mail
        lorsqu’un matching ou une discussion a lieu entre particuliers.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Impossible de charger les réglages messagerie.
        </Alert>
      )}

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Réglages enregistrés.
        </Alert>
      )}

      {isLoading ? (
        <CircularProgress />
      ) : (
        <Stack spacing={3}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Alertes admin (particuliers)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Recevoir un e-mail détaillé à chaque matching entre deux particuliers, et à chaque
              message impliquant un particulier (P2P ou particulier / partenaire). Enregistre
              l&apos;adresse puis utilise le bouton de test.
            </Typography>

            <TextField
              fullWidth
              type="email"
              label="Adresse e-mail de notification"
              value={adminNotifyEmail}
              onChange={(e) => setAdminNotifyEmail(e.target.value)}
              placeholder="ex. alertes@sherlook.app"
              helperText="Laissez vide pour désactiver l’envoi, même si les options ci-dessous sont cochées."
              sx={{ mb: 2 }}
            />

            <Stack spacing={0.5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notifyMatch}
                    onChange={(e) => setNotifyMatch(e.target.checked)}
                  />
                }
                label="Notifier les matchings particulier ↔ particulier"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={notifyMessage}
                    onChange={(e) => setNotifyMessage(e.target.checked)}
                  />
                }
                label="Notifier les discussions (P2P et particulier / partenaire)"
              />
            </Stack>
            <Button
              variant="outlined"
              sx={{ mt: 2 }}
              disabled={testNotifyMutation.isPending || adminNotifyEmail.trim() === ''}
              onClick={() => testNotifyMutation.mutate()}
            >
              {testNotifyMutation.isPending ? 'Envoi du test…' : 'Envoyer un e-mail de test'}
            </Button>
            {testBanner && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {testBanner}
              </Alert>
            )}
            {testNotifyMutation.isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {(testNotifyMutation.error as { response?: { data?: { error?: string } } })?.response?.data?.error
                  || 'Échec envoi du test.'}
              </Alert>
            )}
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Messages type perdeur
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Raccourcis affichés au-dessus du champ de saisie lorsque l&apos;utilisateur est le
              propriétaire de l&apos;objet perdu (perdeur).
            </Typography>

            <Stack spacing={1.5}>
              {loserTemplates.map((template, index) => (
                <Box
                  key={`loser-template-${index}`}
                  sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}
                >
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={4}
                    label={`Message ${index + 1}`}
                    value={template}
                    onChange={(e) => {
                      const next = [...loserTemplates];
                      next[index] = e.target.value;
                      setLoserTemplates(next);
                    }}
                    placeholder="Ex. Bonjour, pouvez-vous me décrire l'objet pour confirmer qu'il s'agit bien du mien ?"
                  />
                  <IconButton
                    aria-label="Supprimer ce message type"
                    onClick={() => setLoserTemplates((prev) => prev.filter((_, i) => i !== index))}
                    sx={{ mt: 1 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddTemplate}
                disabled={loserTemplates.length >= 20}
                sx={{ alignSelf: 'flex-start' }}
              >
                Ajouter un message type
              </Button>
            </Stack>
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </Box>

          {saveMutation.isError && (
            <Alert severity="error">{saveErrorMessage}</Alert>
          )}
        </Stack>
      )}
    </Box>
  );
}
